import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi, budgetApi, posApi, reservationsApi } from '../lib/api';
import api from '../lib/api';
import { getTodayString, getMonthName, formatCurrency, cn } from '../lib/utils';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { KpiCard, KpiCardSmall } from '../components/dashboard/KpiCard';
import { RestaurantSelector } from '../components/dashboard/RestaurantSelector';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
    Euro, 
    TrendingUp, 
    Users, 
    CalendarDays,
    Building2,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    BarChart3,
    Clock,
    Sun,
    Moon,
    Star,
    Utensils,
    Target,
    XCircle,
    Store,
    Download
} from 'lucide-react';
import { 
    PieChart, 
    Pie, 
    Cell, 
    Tooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    LabelList,
    ComposedChart,
    Line,
    ReferenceLine
} from 'recharts';

// Composant pour afficher Budget vs Réalisé avec écarts vs Budget et vs N-1
// Composant pour afficher Budget vs Réalisé avec écarts vs Budget et vs N-1 (avec support BtoB)
const ComparisonCard = ({ label, budget, real, ecartBudget, n1, ecartN1, subtitle, icon: Icon, caisse = 0, btob = 0, restaurantsData = null, dataKeyPrefix = 'ytd_month' }) => {
    const getEcartColor = (ecart) => {
        if (ecart === null || ecart === undefined) return 'text-muted-foreground';
        if (ecart >= 0) return 'text-green-600';
        return 'text-red-600';
    };

    const getEcartIcon = (ecart) => {
        if (ecart === null || ecart === undefined) return Minus;
        if (ecart >= 0) return ArrowUpRight;
        return ArrowDownRight;
    };

    const EcartBudgetIcon = getEcartIcon(ecartBudget);
    const EcartN1Icon = getEcartIcon(ecartN1);
    
    // Détecter si on a des données BtoB
    const hasBtobData = btob > 0;

    // Noms courts pour les restaurants
    const shortNames = {
        "Ma biche sur le toit": "Ma Biche",
        "Compagnie Française": "Cie Française",
        "Compagnie Busca": "Cie Busca",
        "Jacques Balma": "J. Balma",
        "Jacques Blagnac": "J. Blagnac",
        "Café Chouchou": "Chouchou",
        "Coup de bambou": "Bambou"
    };

    // Générer le contenu du tooltip pour l'écart vs Budget par restaurant
    // Exclure Blagnac du budget car pas de données réalisées
    const getBudgetTooltipContent = () => {
        if (!restaurantsData || restaurantsData.length === 0) return null;
        
        const realKey = dataKeyPrefix === 'day' ? 'real_day_ht' : 
                        dataKeyPrefix === 'ytd_month' ? 'real_ytd_month_ht' : 'real_ytd_year_ht';
        const budgetKey = dataKeyPrefix === 'day' ? 'budget_day_ht' : 
                          dataKeyPrefix === 'ytd_month' ? 'budget_ytd_month_ht' : 'budget_ytd_year_ht';
        
        return restaurantsData
            .filter(r => {
                // Exclure Blagnac du budget (pas de données réalisées)
                if (r.restaurant === 'Jacques Blagnac') return false;
                return r[budgetKey] > 0 || r[realKey] > 0;
            })
            .map(r => {
                const rReal = r[realKey] || 0;
                const rBudget = r[budgetKey] || 0;
                const ecart = rBudget > 0 ? ((rReal - rBudget) / rBudget) * 100 : 0;
                const shortName = shortNames[r.restaurant] || r.restaurant;
                return { name: shortName, ecart, real: rReal, budget: rBudget };
            })
            .sort((a, b) => b.ecart - a.ecart);
    };

    // Générer le contenu du tooltip pour l'écart vs N-1 par restaurant
    const getN1TooltipContent = () => {
        if (!restaurantsData || restaurantsData.length === 0) return null;
        
        const realKey = dataKeyPrefix === 'day' ? 'real_day_ht' : 
                        dataKeyPrefix === 'ytd_month' ? 'real_ytd_month_ht' : 'real_ytd_year_ht';
        const n1Key = dataKeyPrefix === 'day' ? 'n1_day_ht' : 
                      dataKeyPrefix === 'ytd_month' ? 'n1_ytd_month_ht' : 'n1_ytd_year_ht';
        
        return restaurantsData
            .filter(r => r[realKey] > 0)  // Afficher tous les restaurants avec du réalisé
            .map(r => {
                const rReal = r[realKey] || 0;
                const rN1 = r[n1Key] || 0;
                // Si pas de N-1, retourner null pour l'écart (affichera "Nouveau")
                const ecart = rN1 > 0 ? ((rReal - rN1) / rN1) * 100 : null;
                const shortName = shortNames[r.restaurant] || r.restaurant;
                const isNew = rN1 === 0;  // Restaurant nouveau (pas de données N-1)
                return { name: shortName, ecart, real: rReal, n1: rN1, isNew };
            })
            // Trier : restaurants avec écart en haut, "Nouveau" en bas
            .sort((a, b) => {
                if (a.ecart === null && b.ecart === null) return 0;
                if (a.ecart === null) return 1;  // "Nouveau" en bas
                if (b.ecart === null) return -1;
                return b.ecart - a.ecart;
            });
    };

    const budgetTooltipData = getBudgetTooltipContent();
    const n1TooltipData = getN1TooltipContent();

    return (
        <div className="bg-card border border-border shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
                <p className="text-sm text-muted-foreground font-medium">{label}</p>
                {Icon && (
                    <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-4 h-4 text-primary" />
                    </div>
                )}
            </div>
            
            <div className="space-y-2">
                {/* Réalisé Total - Mis en avant */}
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">{hasBtobData ? 'Réalisé Total' : 'Réalisé'}</span>
                    <span className="text-lg font-bold text-primary">{formatCurrency(real)}</span>
                </div>
                
                {/* Détail Caisse + BtoB si BtoB présent */}
                {hasBtobData && (
                    <div className="bg-muted/50 rounded-lg p-2 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-violet-500 inline-block"></span>
                                Réalisé Caisse
                            </span>
                            <span className="font-semibold text-violet-600">{formatCurrency(caisse)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
                                Réalisé BtoB
                            </span>
                            <span className="font-semibold text-emerald-600">{formatCurrency(btob)}</span>
                        </div>
                    </div>
                )}
                
                {/* Budget */}
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Budget</span>
                    <span className="text-sm font-medium">{formatCurrency(budget)}</span>
                </div>
                
                {/* N-1 */}
                {n1 !== undefined && (
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">N-1</span>
                        <span className="text-sm font-medium">{formatCurrency(n1)}</span>
                    </div>
                )}
                
                {/* Écarts */}
                <div className="pt-2 border-t border-border space-y-1">
                    {/* Écart vs Budget - avec tooltip par restaurant en mode consolidé */}
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">vs Budget</span>
                        {budgetTooltipData && budgetTooltipData.length > 0 ? (
                            <TooltipProvider>
                                <ShadcnTooltip delayDuration={100}>
                                    <TooltipTrigger asChild>
                                        <div className={cn("flex items-center gap-1 cursor-help", getEcartColor(ecartBudget))}>
                                            <EcartBudgetIcon className="w-3 h-3" />
                                            <span className="text-sm font-bold underline decoration-dotted">
                                                {ecartBudget !== null && ecartBudget !== undefined ? `${ecartBudget >= 0 ? '+' : ''}${ecartBudget.toFixed(1)}%` : '-'}
                                            </span>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="p-3 max-w-xs">
                                        <p className="font-semibold text-xs mb-2 border-b pb-1">Écart vs Budget par restaurant</p>
                                        <div className="space-y-1">
                                            {budgetTooltipData.map((r, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-xs gap-4">
                                                    <span className="text-muted-foreground">{r.name}</span>
                                                    <span className={cn(
                                                        "font-bold",
                                                        r.ecart >= 0 ? "text-green-600" : "text-red-600"
                                                    )}>
                                                        {r.ecart >= 0 ? '+' : ''}{r.ecart.toFixed(1)}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </TooltipContent>
                                </ShadcnTooltip>
                            </TooltipProvider>
                        ) : (
                            <div className={cn("flex items-center gap-1", getEcartColor(ecartBudget))}>
                                <EcartBudgetIcon className="w-3 h-3" />
                                <span className="text-sm font-bold">
                                    {ecartBudget !== null && ecartBudget !== undefined ? `${ecartBudget >= 0 ? '+' : ''}${ecartBudget.toFixed(1)}%` : '-'}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {/* Écart vs N-1 - avec tooltip par restaurant en mode consolidé */}
                    {ecartN1 !== undefined && (
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">vs N-1</span>
                            {n1TooltipData && n1TooltipData.length > 0 ? (
                                <TooltipProvider>
                                    <ShadcnTooltip delayDuration={100}>
                                        <TooltipTrigger asChild>
                                            <div className={cn("flex items-center gap-1 cursor-help", getEcartColor(ecartN1))}>
                                                <EcartN1Icon className="w-3 h-3" />
                                                <span className="text-sm font-bold underline decoration-dotted">
                                                    {ecartN1 !== null ? `${ecartN1 >= 0 ? '+' : ''}${ecartN1.toFixed(1)}%` : '-'}
                                                </span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="p-3 max-w-xs">
                                            <p className="font-semibold text-xs mb-2 border-b pb-1">Écart vs N-1 par restaurant</p>
                                            <div className="space-y-1">
                                                {n1TooltipData.map((r, idx) => (
                                                    <div key={idx} className="flex justify-between items-center text-xs gap-4">
                                                        <span className="text-muted-foreground">{r.name}</span>
                                                        {r.isNew ? (
                                                            <span className="font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">
                                                                Nouveau
                                                            </span>
                                                        ) : (
                                                            <span className={cn(
                                                                "font-bold",
                                                                r.ecart >= 0 ? "text-green-600" : "text-red-600"
                                                            )}>
                                                                {r.ecart >= 0 ? '+' : ''}{r.ecart.toFixed(1)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </TooltipContent>
                                    </ShadcnTooltip>
                                </TooltipProvider>
                            ) : (
                                <div className={cn("flex items-center gap-1", getEcartColor(ecartN1))}>
                                    <EcartN1Icon className="w-3 h-3" />
                                    <span className="text-sm font-bold">
                                        {ecartN1 !== null ? `${ecartN1 >= 0 ? '+' : ''}${ecartN1.toFixed(1)}%` : '-'}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {subtitle && (
                <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
            )}
        </div>
    );
};

// Composant spécial pour le CA du jour avec jauges de progression et BtoB
const ComparisonCardWithGauge = ({ label, budget, real, realTtc, n1, n1Ttc, icon: Icon, displayMode = 'HT', caisse = 0, btob = 0, caisseTtc = 0, btobTtc = 0, restaurantsData = null, dataKeyPrefix = 'day' }) => {
    // Utiliser HT ou TTC selon le mode
    const displayReal = displayMode === 'TTC' && realTtc ? realTtc : real;
    const displayN1 = displayMode === 'TTC' && n1Ttc ? n1Ttc : n1;
    const displayCaisse = displayMode === 'TTC' && caisseTtc ? caisseTtc : caisse;
    const displayBtob = displayMode === 'TTC' && btobTtc ? btobTtc : btob;
    const suffix = displayMode === 'TTC' ? 'TTC' : 'HT';
    
    // Calcul des pourcentages de progression (toujours basé sur HT pour le budget)
    const progressBudget = budget > 0 ? Math.min((real / budget) * 100, 100) : 0;
    const progressN1 = n1 > 0 ? Math.min((real / n1) * 100, 150) : 0; // Peut dépasser 100%
    
    // Couleurs des jauges
    const getBudgetColor = () => {
        if (progressBudget >= 100) return 'bg-green-500';
        if (progressBudget >= 75) return 'bg-yellow-500';
        if (progressBudget >= 50) return 'bg-orange-500';
        return 'bg-red-500';
    };
    
    const getN1Color = () => {
        const ratio = n1 > 0 ? (real / n1) * 100 : 0;
        if (ratio >= 100) return 'bg-green-500';
        if (ratio >= 90) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    // Dynamically update label with HT/TTC
    const dynamicLabel = label.replace(/HT|TTC/g, '').trim() + ` ${suffix}`;

    // Détecter si on a des données BtoB
    const hasBtobData = btob > 0;

    // Noms courts pour les restaurants
    const shortNames = {
        "Ma biche sur le toit": "Ma Biche",
        "Compagnie Française": "Cie Française",
        "Compagnie Busca": "Cie Busca",
        "Jacques Balma": "J. Balma",
        "Jacques Blagnac": "J. Blagnac",
        "Café Chouchou": "Chouchou",
        "Coup de bambou": "Bambou"
    };

    // Générer le contenu du tooltip pour l'écart vs Budget par restaurant
    // Exclure Blagnac du budget car pas de données réalisées
    const getBudgetTooltipContent = () => {
        if (!restaurantsData || restaurantsData.length === 0) return null;
        
        const realKey = dataKeyPrefix === 'day' ? 'real_day_ht' : 
                        dataKeyPrefix === 'ytd_month' ? 'real_ytd_month_ht' : 'real_ytd_year_ht';
        const budgetKey = dataKeyPrefix === 'day' ? 'budget_day_ht' : 
                          dataKeyPrefix === 'ytd_month' ? 'budget_ytd_month_ht' : 'budget_ytd_year_ht';
        
        return restaurantsData
            .filter(r => {
                // Exclure Blagnac du budget (pas de données réalisées)
                if (r.restaurant === 'Jacques Blagnac') return false;
                return r[budgetKey] > 0 || r[realKey] > 0;
            })
            .map(r => {
                const rReal = r[realKey] || 0;
                const rBudget = r[budgetKey] || 0;
                const ecart = rBudget > 0 ? ((rReal - rBudget) / rBudget) * 100 : 0;
                const shortName = shortNames[r.restaurant] || r.restaurant;
                return { name: shortName, ecart, real: rReal, budget: rBudget };
            })
            .sort((a, b) => b.ecart - a.ecart);
    };

    // Générer le contenu du tooltip pour l'écart vs N-1 par restaurant
    const getN1TooltipContent = () => {
        if (!restaurantsData || restaurantsData.length === 0) return null;
        
        const realKey = dataKeyPrefix === 'day' ? 'real_day_ht' : 
                        dataKeyPrefix === 'ytd_month' ? 'real_ytd_month_ht' : 'real_ytd_year_ht';
        const n1Key = dataKeyPrefix === 'day' ? 'n1_day_ht' : 
                      dataKeyPrefix === 'ytd_month' ? 'n1_ytd_month_ht' : 'n1_ytd_year_ht';
        
        return restaurantsData
            .filter(r => r[realKey] > 0)  // Afficher tous les restaurants avec du réalisé
            .map(r => {
                const rReal = r[realKey] || 0;
                const rN1 = r[n1Key] || 0;
                // Si pas de N-1, retourner null pour l'écart (affichera "Nouveau")
                const ecart = rN1 > 0 ? ((rReal - rN1) / rN1) * 100 : null;
                const shortName = shortNames[r.restaurant] || r.restaurant;
                const isNew = rN1 === 0;  // Restaurant nouveau (pas de données N-1)
                return { name: shortName, ecart, real: rReal, n1: rN1, isNew };
            })
            // Trier : restaurants avec écart en haut, "Nouveau" en bas
            .sort((a, b) => {
                if (a.ecart === null && b.ecart === null) return 0;
                if (a.ecart === null) return 1;  // "Nouveau" en bas
                if (b.ecart === null) return -1;
                return b.ecart - a.ecart;
            });
    };

    const budgetTooltipData = getBudgetTooltipContent();
    const n1TooltipData = getN1TooltipContent();

    return (
        <div className="bg-card border border-border shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
                <p className="text-sm text-muted-foreground font-medium">{dynamicLabel}</p>
                {Icon && (
                    <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-4 h-4 text-primary" />
                    </div>
                )}
            </div>
            
            <div className="space-y-3">
                {/* Réalisé Total - Mis en avant */}
                <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Réalisé Total</span>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(displayReal)}</span>
                </div>
                
                {/* Détail Caisse + BtoB si BtoB présent */}
                {hasBtobData && (
                    <div className="bg-muted/50 rounded-lg p-2 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-violet-500 inline-block"></span>
                                Réalisé Caisse
                            </span>
                            <span className="font-semibold text-violet-600">{formatCurrency(displayCaisse)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
                                Réalisé BtoB
                            </span>
                            <span className="font-semibold text-emerald-600">{formatCurrency(displayBtob)}</span>
                        </div>
                    </div>
                )}
                
                {/* Jauge Budget (toujours en HT) - avec tooltip par restaurant */}
                <div className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground">vs Budget (HT)</span>
                        <span className="font-medium">{formatCurrency(budget)}</span>
                    </div>
                    <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                            className={cn("h-full rounded-full transition-all duration-500", getBudgetColor())}
                            style={{ width: `${Math.min(progressBudget, 100)}%` }}
                        />
                        {/* Indicateur 100% */}
                        <div className="absolute right-0 top-0 h-full w-0.5 bg-border" />
                    </div>
                    <div className="flex justify-between items-center text-xs">
                        {budgetTooltipData && budgetTooltipData.length > 0 ? (
                            <TooltipProvider>
                                <ShadcnTooltip delayDuration={100}>
                                    <TooltipTrigger asChild>
                                        <span className={cn(
                                            "font-bold cursor-help underline decoration-dotted",
                                            progressBudget >= 100 ? "text-green-600" : "text-muted-foreground"
                                        )}>
                                            {progressBudget.toFixed(0)}%
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="p-3 max-w-xs">
                                        <p className="font-semibold text-xs mb-2 border-b pb-1">Écart vs Budget par restaurant</p>
                                        <div className="space-y-1">
                                            {budgetTooltipData.map((r, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-xs gap-4">
                                                    <span className="text-muted-foreground">{r.name}</span>
                                                    <span className={cn(
                                                        "font-bold",
                                                        r.ecart >= 0 ? "text-green-600" : "text-red-600"
                                                    )}>
                                                        {r.ecart >= 0 ? '+' : ''}{r.ecart.toFixed(1)}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </TooltipContent>
                                </ShadcnTooltip>
                            </TooltipProvider>
                        ) : (
                            <span className={cn(
                                "font-bold",
                                progressBudget >= 100 ? "text-green-600" : "text-muted-foreground"
                            )}>
                                {progressBudget.toFixed(0)}%
                            </span>
                        )}
                        <span className="text-muted-foreground">100%</span>
                    </div>
                </div>
                
                {/* Jauge N-1 */}
                {n1 !== undefined && n1 > 0 && (
                    <div className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-muted-foreground">vs N-1</span>
                            <span className="font-medium">{formatCurrency(displayN1)}</span>
                        </div>
                        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                            <div 
                                className={cn("h-full rounded-full transition-all duration-500", getN1Color())}
                                style={{ width: `${Math.min(progressN1, 100)}%` }}
                            />
                            {/* Indicateur 100% */}
                            <div className="absolute right-0 top-0 h-full w-0.5 bg-border" />
                        </div>
                        <div className="flex justify-between items-center text-xs">
                            {n1TooltipData && n1TooltipData.length > 0 ? (
                                <TooltipProvider>
                                    <ShadcnTooltip delayDuration={100}>
                                        <TooltipTrigger asChild>
                                            <span className={cn(
                                                "font-bold cursor-help underline decoration-dotted",
                                                progressN1 >= 100 ? "text-green-600" : "text-muted-foreground"
                                            )}>
                                                {progressN1.toFixed(0)}%
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="p-3 max-w-xs">
                                            <p className="font-semibold text-xs mb-2 border-b pb-1">Écart vs N-1 par restaurant</p>
                                            <div className="space-y-1">
                                                {n1TooltipData.map((r, idx) => (
                                                    <div key={idx} className="flex justify-between items-center text-xs gap-4">
                                                        <span className="text-muted-foreground">{r.name}</span>
                                                        {r.isNew ? (
                                                            <span className="font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">
                                                                Nouveau
                                                            </span>
                                                        ) : (
                                                            <span className={cn(
                                                                "font-bold",
                                                                r.ecart >= 0 ? "text-green-600" : "text-red-600"
                                                            )}>
                                                                {r.ecart >= 0 ? '+' : ''}{r.ecart.toFixed(1)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </TooltipContent>
                                    </ShadcnTooltip>
                                </TooltipProvider>
                            ) : (
                                <span className={cn(
                                    "font-bold",
                                    progressN1 >= 100 ? "text-green-600" : "text-muted-foreground"
                                )}>
                                    {progressN1.toFixed(0)}%
                                </span>
                            )}
                            <span className="text-muted-foreground">100%</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Composant pour afficher le CA en cours (tables ouvertes non encaissées)
const OpenTablesCard = ({ data, loading }) => {
    if (loading) {
        return <Skeleton className="h-24 rounded-xl" />;
    }
    
    if (!data || !data.available) {
        return null;
    }
    
    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200" data-testid="open-tables-card">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        CA en cours (tables ouvertes)
                    </p>
                    <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="open-tables-total">
                        {formatCurrency(data.total_open)}
                    </p>
                </div>
                <div className="text-right">
                    <span className="text-sm font-semibold bg-amber-500/20 text-amber-700 px-2 py-1 rounded-full" data-testid="open-tables-count">
                        {data.open_tables_count} {data.open_tables_count > 1 ? 'tables' : 'table'}
                    </span>
                </div>
            </div>
            
            {/* Liste des tables (max 5 affichées) */}
            {data.tables && data.tables.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                    <div className="space-y-1">
                        {data.tables.slice(0, 5).map((table, index) => (
                            <div key={index} className="flex justify-between items-center text-xs">
                                <span className="text-muted-foreground">
                                    {table.table} {table.operator && `(${table.operator})`}
                                </span>
                                <span className="font-medium">{formatCurrency(table.total)}</span>
                            </div>
                        ))}
                        {data.tables.length > 5 && (
                            <p className="text-xs text-muted-foreground italic">
                                + {data.tables.length - 5} autres tables...
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Composant pour afficher Budget BtoB vs Réalisé BtoB
const BtoBBudgetCard = ({ comparisonData }) => {
    if (!comparisonData) return null;
    
    // Vérifier si des données BtoB sont disponibles
    const hasBudget = (comparisonData.btob_budget_ytd_month_ht || 0) > 0 || (comparisonData.btob_budget_ytd_ht || 0) > 0;
    const hasBtobData = (comparisonData.btob_ytd_month_ht || 0) > 0 || (comparisonData.btob_ytd_year_ht || 0) > 0;
    const hasN1Data = (comparisonData.n1_btob_ytd_month_ht || 0) > 0 || (comparisonData.n1_btob_ytd_year_ht || 0) > 0;
    
    if (!hasBudget && !hasBtobData && !hasN1Data) return null;
    
    // YTD Mensuel
    const budgetYtdMonth = comparisonData.btob_budget_ytd_month_ht || 0;
    const actualYtdMonth = comparisonData.btob_ytd_month_ht || 0;
    const n1YtdMonth = comparisonData.n1_btob_ytd_month_ht || 0;
    
    // YTD Annuel
    const budgetYtdYear = comparisonData.btob_budget_ytd_ht || 0;
    const actualYtdYear = comparisonData.btob_ytd_year_ht || 0;
    const n1YtdYear = comparisonData.n1_btob_ytd_year_ht || 0;
    
    // Calcul des écarts vs Budget
    const ecartBudgetMonth = budgetYtdMonth > 0 ? ((actualYtdMonth - budgetYtdMonth) / budgetYtdMonth) * 100 : null;
    const ecartBudgetYear = budgetYtdYear > 0 ? ((actualYtdYear - budgetYtdYear) / budgetYtdYear) * 100 : null;
    
    // Calcul des écarts vs N-1
    const ecartN1Month = n1YtdMonth > 0 ? ((actualYtdMonth - n1YtdMonth) / n1YtdMonth) * 100 : null;
    const ecartN1Year = n1YtdYear > 0 ? ((actualYtdYear - n1YtdYear) / n1YtdYear) * 100 : null;
    
    const getEcartColor = (ecart) => {
        if (ecart === null) return 'text-muted-foreground';
        if (ecart >= 0) return 'text-green-600';
        return 'text-red-600';
    };
    
    const formatEcart = (ecart) => {
        if (ecart === null) return '-';
        return `${ecart >= 0 ? '+' : ''}${ecart.toFixed(1)}%`;
    };
    
    return (
        <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200" data-testid="btob-budget-card">
            <div className="flex items-start justify-between mb-3">
                <p className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-500" />
                    BtoB Réalisé vs Budget vs N-1
                </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                {/* YTD Mensuel */}
                <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase">YTD Mensuel</p>
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Réalisé</span>
                            <span className="text-sm font-bold text-emerald-600">{formatCurrency(actualYtdMonth)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Budget</span>
                            <span className="text-sm font-medium">{formatCurrency(budgetYtdMonth)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">N-1</span>
                            <span className="text-sm font-medium text-slate-500">{formatCurrency(n1YtdMonth)}</span>
                        </div>
                        <div className="pt-1.5 border-t border-emerald-500/20 space-y-1">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground">vs Budget</span>
                                <span className={cn("text-xs font-bold", getEcartColor(ecartBudgetMonth))}>
                                    {formatEcart(ecartBudgetMonth)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground">vs N-1</span>
                                <span className={cn("text-xs font-bold", getEcartColor(ecartN1Month))}>
                                    {formatEcart(ecartN1Month)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* YTD Annuel */}
                <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase">YTD Annuel</p>
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Réalisé</span>
                            <span className="text-sm font-bold text-emerald-600">{formatCurrency(actualYtdYear)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Budget</span>
                            <span className="text-sm font-medium">{formatCurrency(budgetYtdYear)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">N-1</span>
                            <span className="text-sm font-medium text-slate-500">{formatCurrency(n1YtdYear)}</span>
                        </div>
                        <div className="pt-1.5 border-t border-emerald-500/20 space-y-1">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground">vs Budget</span>
                                <span className={cn("text-xs font-bold", getEcartColor(ecartBudgetYear))}>
                                    {formatEcart(ecartBudgetYear)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-muted-foreground">vs N-1</span>
                                <span className={cn("text-xs font-bold", getEcartColor(ecartN1Year))}>
                                    {formatEcart(ecartN1Year)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Composant pour afficher les données Forecast
const ForecastCard = ({ forecastData, label, subtitle, icon: Icon, isMonth }) => {
    if (!forecastData) return null;
    
    const forecast = isMonth ? forecastData.forecastMonth : forecastData.forecastYear;
    const budget = isMonth ? forecastData.budgetMonthTotal : forecastData.budgetYearTotal;
    const daysRemaining = isMonth ? forecastData.daysRemaining : forecastData.daysRemainingYear;
    const tauxAtteinte = isMonth ? forecastData.tauxAtteinteMensuel : forecastData.tauxAtteinteAnnuel;
    const ponderation = isMonth ? forecastData.ponderationMensuelle : forecastData.ponderationAnnuelle;
    
    const getTauxColor = (value) => {
        if (value >= 100) return 'text-green-600';
        if (value >= 90) return 'text-amber-600';
        return 'text-red-600';
    };
    
    // Couleur de l'atterrissage selon si on atteint le budget ou pas
    const getForecastColor = () => {
        if (forecast >= budget) return 'text-green-600';
        if (forecast >= budget * 0.9) return 'text-amber-600';
        return 'text-red-600';
    };
    
    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200" data-testid={`forecast-card-${isMonth ? 'month' : 'year'}`}>
            <div className="flex items-start justify-between mb-3">
                <div>
                    <p className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                        {Icon && <Icon className="w-4 h-4 text-amber-600" />}
                        {label}
                    </p>
                    {subtitle && (
                        <p className="text-xs text-amber-600 mt-0.5">{subtitle}</p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                        {daysRemaining}j restants
                    </span>
                    <span className="text-xs text-muted-foreground">
                        Tendance: {ponderation?.toFixed(0) || 0}%
                    </span>
                </div>
            </div>
            
            <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                    <span className="text-xs text-muted-foreground uppercase">Atterrissage prévu</span>
                    <span className={cn("text-2xl font-bold", getForecastColor())}>{formatCurrency(forecast)}</span>
                </div>
                
                <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Budget cible</span>
                        <span className="text-sm font-medium">{formatCurrency(budget)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-1.5 border-t border-amber-500/20">
                        <span className="text-xs text-muted-foreground">Taux d'atteinte actuel</span>
                        <span className={cn("text-sm font-bold", getTauxColor(tauxAtteinte))}>
                            {tauxAtteinte?.toFixed(1) || 0}%
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Mapping restaurant name to Zenchef data
const ZENCHEF_RESTAURANT_MAP = {
    'Ma biche sur le toit': 'Ma biche sur le toit',
    'Compagnie Française': 'Compagnie Française',
    'Compagnie Busca': 'Compagnie Busca',
    'Jacques Balma': 'Jacques Balma',
    'Café Chouchou': 'Café Chouchou',
    'Sous les pins': 'Sous les pins'
};

// Composant VIP Tooltip
const VipTooltipContent = ({ vips }) => {
    if (!vips || vips.length === 0) {
        return <p className="text-xs text-muted-foreground">Aucun VIP</p>;
    }
    
    return (
        <div className="space-y-2 min-w-[180px]">
            <p className="font-semibold text-sm border-b pb-1 mb-2">
                {vips.length} Client{vips.length > 1 ? 's' : ''} VIP
            </p>
            {vips.map((vip, idx) => (
                <div key={idx} className="flex justify-between items-center gap-4 py-1">
                    <div className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-amber-500" />
                        <span className="font-medium text-sm">{vip.name || 'Client VIP'}</span>
                    </div>
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded whitespace-nowrap">
                        {vip.visit_count > 0 ? `${vip.visit_count} visites` : 'VIP'}
                    </span>
                </div>
            ))}
        </div>
    );
};

// Composant pour afficher les réservations Zenchef
const ReservationsCard = ({ data, vipData, restaurantName, isConsolidated, loading, lastUpdate }) => {
    if (loading) {
        return <Skeleton className="h-32 rounded-xl" />;
    }
    
    // Filtrer les données selon le restaurant ou tout afficher si consolidé
    const getReservationData = () => {
        if (!data?.restaurants) return null;
        
        if (isConsolidated) {
            return {
                total_covers: data.total_covers || 0,
                covers_midi: data.total_covers_midi || 0,
                covers_soir: data.total_covers_soir || 0,
                total_reservations: data.total_reservations || 0,
                cancelled_total: data.total_cancelled || 0,
                cancelled_covers_total: data.total_cancelled_covers || 0,
                cancellation_rate: data.cancellation_rate || 0
            };
        }
        
        const zenchefName = ZENCHEF_RESTAURANT_MAP[restaurantName];
        if (!zenchefName) return null;
        
        const resto = data.restaurants.find(r => r.restaurant === zenchefName);
        return resto || null;
    };
    
    const getVipData = () => {
        if (!vipData?.vips) return [];
        
        if (isConsolidated) {
            return vipData.vips;
        }
        
        const zenchefName = ZENCHEF_RESTAURANT_MAP[restaurantName];
        if (!zenchefName) return [];
        
        return vipData.vips.filter(v => v.restaurant === zenchefName);
    };
    
    const resaData = getReservationData();
    const vips = getVipData();
    const vipsMidi = vips.filter(v => v.meal_period === 'midi');
    const vipsSoir = vips.filter(v => v.meal_period === 'soir');
    
    // Si pas de Zenchef pour ce restaurant et pas consolidé
    if (!resaData && !isConsolidated) {
        return null;
    }
    
    // Si pas de données mais Zenchef existe
    if (!resaData) {
        return (
            <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 shadow-sm p-4 rounded-xl" data-testid="reservations-card">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Utensils className="w-4 h-4" />
                    <span className="text-sm">Aucune réservation pour ce jour</span>
                </div>
            </div>
        );
    }
    
    return (
        <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200" data-testid="reservations-card">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <p className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                        <Utensils className="w-4 h-4 text-violet-500" />
                        Réservations du jour {isConsolidated ? '(Groupe)' : ''}
                    </p>
                    {lastUpdate && (
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                            Màj: {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                    )}
                </div>
                {vips.length > 0 && (
                    <TooltipProvider delayDuration={100}>
                        <ShadcnTooltip>
                            <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full cursor-help hover:bg-amber-100 transition-colors">
                                    <Star className="w-3 h-3" />
                                    {vips.length} VIP
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                <VipTooltipContent vips={vips} />
                            </TooltipContent>
                        </ShadcnTooltip>
                    </TooltipProvider>
                )}
            </div>
            
            {/* Total couverts */}
            <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold text-violet-600" data-testid="total-covers">
                    {resaData.total_covers || resaData.covers_total || 0}
                </span>
                <span className="text-sm text-muted-foreground">couverts</span>
            </div>
            
            {/* Midi / Soir avec tooltips VIP */}
            <div className="grid grid-cols-2 gap-3">
                <TooltipProvider delayDuration={100}>
                    <ShadcnTooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center justify-between bg-background/50 rounded-lg p-2 cursor-help hover:bg-background/80 transition-colors">
                                <div className="flex items-center gap-2">
                                    <Sun className="w-4 h-4 text-yellow-500" />
                                    <span className="text-xs text-muted-foreground">Midi</span>
                                    {vipsMidi.length > 0 && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
                                            <Star className="w-2.5 h-2.5" />
                                            {vipsMidi.length}
                                        </span>
                                    )}
                                </div>
                                <span className="font-semibold text-sm">{resaData.covers_midi || 0}</span>
                            </div>
                        </TooltipTrigger>
                        {vipsMidi.length > 0 && (
                            <TooltipContent side="top" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                <VipTooltipContent vips={vipsMidi} />
                            </TooltipContent>
                        )}
                    </ShadcnTooltip>
                </TooltipProvider>
                
                <TooltipProvider delayDuration={100}>
                    <ShadcnTooltip>
                        <TooltipTrigger asChild>
                            <div className="flex items-center justify-between bg-background/50 rounded-lg p-2 cursor-help hover:bg-background/80 transition-colors">
                                <div className="flex items-center gap-2">
                                    <Moon className="w-4 h-4 text-indigo-500" />
                                    <span className="text-xs text-muted-foreground">Soir</span>
                                    {vipsSoir.length > 0 && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
                                            <Star className="w-2.5 h-2.5" />
                                            {vipsSoir.length}
                                        </span>
                                    )}
                                </div>
                                <span className="font-semibold text-sm">{resaData.covers_soir || 0}</span>
                            </div>
                        </TooltipTrigger>
                        {vipsSoir.length > 0 && (
                            <TooltipContent side="top" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                <VipTooltipContent vips={vipsSoir} />
                            </TooltipContent>
                        )}
                    </ShadcnTooltip>
                </TooltipProvider>
            </div>
            
            {/* Taux d'annulation */}
            {(resaData.cancelled_total > 0 || resaData.cancellation_rate > 0) && (
                <div className="mt-3 pt-3 border-t border-violet-200/50">
                    <TooltipProvider delayDuration={100}>
                        <ShadcnTooltip>
                            <TooltipTrigger asChild>
                                <div className="flex items-center justify-between cursor-help hover:bg-background/50 rounded-lg p-1.5 -m-1.5 transition-colors">
                                    <div className="flex items-center gap-2">
                                        <XCircle className="w-4 h-4 text-red-400" />
                                        <span className="text-xs text-muted-foreground">Annulées</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-red-500 font-medium">
                                            {resaData.cancelled_total || 0} rés.
                                        </span>
                                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                            resaData.cancellation_rate > 15 
                                                ? 'bg-red-100 text-red-600' 
                                                : resaData.cancellation_rate > 8 
                                                    ? 'bg-amber-100 text-amber-600'
                                                    : 'bg-green-100 text-green-600'
                                        }`}>
                                            {resaData.cancellation_rate || 0}%
                                        </span>
                                    </div>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="bg-white dark:bg-gray-900 border shadow-xl p-3 z-50">
                                <div className="text-xs space-y-1">
                                    <p className="font-medium">Détail des annulations</p>
                                    <p className="text-muted-foreground">
                                        {resaData.cancelled_total || 0} réservation{(resaData.cancelled_total || 0) > 1 ? 's' : ''} annulée{(resaData.cancelled_total || 0) > 1 ? 's' : ''}
                                    </p>
                                    <p className="text-muted-foreground">
                                        {resaData.cancelled_covers_total || 0} couvert{(resaData.cancelled_covers_total || 0) > 1 ? 's' : ''} perdu{(resaData.cancelled_covers_total || 0) > 1 ? 's' : ''}
                                    </p>
                                    <p className={`font-medium ${
                                        resaData.cancellation_rate > 15 ? 'text-red-600' : 
                                        resaData.cancellation_rate > 8 ? 'text-amber-600' : 'text-green-600'
                                    }`}>
                                        Taux: {resaData.cancellation_rate || 0}% {
                                            resaData.cancellation_rate > 15 ? '(élevé)' : 
                                            resaData.cancellation_rate > 8 ? '(moyen)' : '(normal)'
                                        }
                                    </p>
                                </div>
                            </TooltipContent>
                        </ShadcnTooltip>
                    </TooltipProvider>
                </div>
            )}
            
            {/* Liste des VIP si présents */}
            {vips.length > 0 && (
                <div className="mt-3 pt-3 border-t border-violet-200/50">
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500" />
                        Clients VIP attendus
                    </p>
                    <div className="space-y-1">
                        {vips.slice(0, 3).map((vip, idx) => (
                            <div key={idx} className="flex justify-between items-center text-xs">
                                <span className="font-medium truncate max-w-[120px]">{vip.name || 'VIP'}</span>
                                <span className="text-amber-600">
                                    {vip.visit_count > 0 ? `${vip.visit_count} visites` : 'VIP'}
                                </span>
                            </div>
                        ))}
                        {vips.length > 3 && (
                            <p className="text-xs text-muted-foreground italic">
                                + {vips.length - 3} autres VIP...
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const DashboardPage = () => {
    const { user, isDirection, canViewConsolidated } = useAuth();
    const [selectedDate, setSelectedDate] = useState(getTodayString());
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);
    const [consolidatedData, setConsolidatedData] = useState(null);
    const [consolidatedComparison, setConsolidatedComparison] = useState(null);
    const [monthlyData, setMonthlyData] = useState(null);
    const [isConsolidatedMode, setIsConsolidatedMode] = useState(false);
    const [chartCompareMode, setChartCompareMode] = useState('budget'); // 'budget' or 'n1'
    const [loading, setLoading] = useState(true);
    const [loadingComparison, setLoadingComparison] = useState(false);
    const [loadingConsolidated, setLoadingConsolidated] = useState(false);
    const [displayMode, setDisplayMode] = useState('HT');
    const [openTablesData, setOpenTablesData] = useState(null);
    const [loadingOpenTables, setLoadingOpenTables] = useState(false);
    const [reservationsData, setReservationsData] = useState(null);
    const [vipData, setVipData] = useState(null);
    const [loadingReservations, setLoadingReservations] = useState(false);
    const [reservationsLastUpdate, setReservationsLastUpdate] = useState(null);
    const [forecastMode, setForecastMode] = useState(false); // Toggle pour afficher le forecast
    const [ventesInternesData, setVentesInternesData] = useState(null);
    const [loadingVentesInternes, setLoadingVentesInternes] = useState(false);

    // Fetch restaurants
    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                setRestaurants(response.data);
                
                if (response.data.length > 0) {
                    // Ma biche sur le toit par défaut
                    const maBiche = response.data.find(r => r.name === "Ma biche sur le toit");
                    if (maBiche) {
                        setSelectedRestaurant(maBiche);
                    } else {
                        setSelectedRestaurant(response.data[0]);
                    }
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error);
                toast.error('Erreur lors du chargement des restaurants');
            }
        };
        fetchRestaurants();
    }, [user?.restaurant_ids]);

    // Fetch dashboard data
    const fetchDashboardData = useCallback(async () => {
        if (!selectedRestaurant) return;
        
        setLoading(true);
        try {
            const response = await budgetApi.getDashboard(selectedRestaurant.name, selectedDate);
            setDashboardData(response.data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [selectedRestaurant, selectedDate]);

    // Fetch comparison data (Budget vs Réalisé) pour tous les restaurants
    const fetchComparisonData = useCallback(async () => {
        // Liste des restaurants avec intégration POS active (pour données temps réel)
        const restaurantsWithPOS = ["Ma biche sur le toit", "Jacques Balma", "Coup de bambou", "Compagnie Française", "Compagnie Busca", "Café Chouchou", "Sous les pins"];
        
        if (!selectedRestaurant) {
            setComparisonData(null);
            return;
        }
        
        setLoadingComparison(true);
        try {
            const response = await budgetApi.getComparison(selectedRestaurant.name, selectedDate);
            setComparisonData(response.data);
        } catch (error) {
            console.error('Error fetching comparison:', error);
            setComparisonData(null);
        } finally {
            setLoadingComparison(false);
        }
    }, [selectedRestaurant, selectedDate]);

    // Fetch consolidated data for users with consolidated access
    const fetchConsolidatedData = useCallback(async () => {
        if (!canViewConsolidated) return;
        
        setLoadingConsolidated(true);
        try {
            const response = await budgetApi.getConsolidated(selectedDate);
            setConsolidatedData(response.data);
            
            // Calculate consolidated comparison totals (en utilisant les données serveur si disponibles)
            if (response.data?.restaurants) {
                const restos = response.data.restaurants;
                const totalRealDay = response.data.total_real_day_ht || restos.reduce((sum, r) => sum + (r.real_day_ht || 0), 0);
                const totalBudgetDay = restos.reduce((sum, r) => sum + (r.budget_day_ht || 0), 0);
                const totalN1Day = response.data.total_n1_day_ht || restos.reduce((sum, r) => sum + (r.n1_day_ht || 0), 0);
                const totalRealYtdMonth = response.data.total_real_ytd_month_ht || restos.reduce((sum, r) => sum + (r.real_ytd_month_ht || 0), 0);
                const totalBudgetYtdMonth = restos.reduce((sum, r) => sum + (r.budget_ytd_month_ht || 0), 0);
                const totalN1YtdMonth = response.data.total_n1_ytd_month_ht || restos.reduce((sum, r) => sum + (r.n1_ytd_month_ht || 0), 0);
                const totalRealYtdYear = response.data.total_real_ytd_year_ht || restos.reduce((sum, r) => sum + (r.real_ytd_year_ht || 0), 0);
                const totalBudgetYtdYear = restos.reduce((sum, r) => sum + (r.budget_ytd_year_ht || 0), 0);
                const totalN1YtdYear = response.data.total_n1_ytd_year_ht || restos.reduce((sum, r) => sum + (r.n1_ytd_year_ht || 0), 0);
                
                // Budget mensuel total (somme des budgets mensuels complets de tous les restaurants)
                const totalBudgetMonthHt = response.data.total_month_ht || restos.reduce((sum, r) => sum + (r.budget_month_ht || 0), 0);
                
                // TTC values - utiliser les totaux du serveur si disponibles
                const totalRealDayTtc = response.data.total_real_day_ttc || restos.reduce((sum, r) => sum + (r.real_day_ttc || 0), 0);
                const totalN1DayTtc = restos.reduce((sum, r) => sum + (r.n1_day_ttc || 0), 0) || (totalN1Day * 1.10);
                const totalRealYtdMonthTtc = response.data.total_real_ytd_month_ttc || restos.reduce((sum, r) => sum + (r.real_ytd_month_ttc || 0), 0);
                const totalN1YtdMonthTtc = restos.reduce((sum, r) => sum + (r.n1_ytd_month_ttc || 0), 0) || (totalN1YtdMonth * 1.10);
                const totalRealYtdYearTtc = response.data.total_real_ytd_year_ttc || restos.reduce((sum, r) => sum + (r.real_ytd_year_ttc || 0), 0);
                const totalN1YtdYearTtc = restos.reduce((sum, r) => sum + (r.n1_ytd_year_ttc || 0), 0) || (totalN1YtdYear * 1.10);
                
                // BtoB/Caisse consolidés (du serveur)
                const totalCaisseDay = response.data.total_caisse_day_ht || 0;
                const totalBtobDay = response.data.total_btob_day_ht || 0;
                const totalCaisseDayTtc = response.data.total_caisse_day_ttc || (totalCaisseDay * 1.10);
                const totalBtobDayTtc = response.data.total_btob_day_ttc || (totalBtobDay * 1.10);
                const totalCaisseYtdMonth = response.data.total_caisse_ytd_month_ht || 0;
                const totalCaisseYtdMonthTtc = response.data.total_caisse_ytd_month_ttc || (totalCaisseYtdMonth * 1.10);
                const totalBtobYtdMonth = response.data.total_btob_ytd_month_ht || 0;
                const totalBtobYtdMonthTtc = totalBtobYtdMonth * 1.10;
                const totalCaisseYtdYear = response.data.total_caisse_ytd_year_ht || 0;
                const totalCaisseYtdYearTtc = response.data.total_caisse_ytd_year_ttc || (totalCaisseYtdYear * 1.10);
                const totalBtobYtdYear = response.data.total_btob_ytd_year_ht || 0;
                const totalBtobYtdYearTtc = totalBtobYtdYear * 1.10;
                
                setConsolidatedComparison({
                    budget_day_ht: totalBudgetDay,
                    real_day_ht: totalRealDay,
                    real_day_ttc: totalRealDayTtc,
                    caisse_day_ht: totalCaisseDay,
                    caisse_day_ttc: totalCaisseDayTtc,
                    btob_day_ht: totalBtobDay,
                    btob_day_ttc: totalBtobDayTtc,
                    n1_day_ht: totalN1Day,
                    n1_day_ttc: totalN1DayTtc,
                    budget_ytd_month_ht: totalBudgetYtdMonth,
                    budget_month_ht: totalBudgetMonthHt, // Budget mensuel total (mois complet)
                    real_ytd_month_ht: totalRealYtdMonth,
                    real_ytd_month_ttc: totalRealYtdMonthTtc,
                    caisse_ytd_month_ht: totalCaisseYtdMonth,
                    caisse_ytd_month_ttc: totalCaisseYtdMonthTtc,
                    btob_ytd_month_ht: totalBtobYtdMonth,
                    btob_ytd_month_ttc: totalBtobYtdMonthTtc,
                    n1_ytd_month_ht: totalN1YtdMonth,
                    n1_ytd_month_ttc: totalN1YtdMonthTtc,
                    budget_ytd_year_ht: totalBudgetYtdYear,
                    real_ytd_year_ht: totalRealYtdYear,
                    real_ytd_year_ttc: totalRealYtdYearTtc,
                    caisse_ytd_year_ht: totalCaisseYtdYear,
                    caisse_ytd_year_ttc: totalCaisseYtdYearTtc,
                    btob_ytd_year_ht: totalBtobYtdYear,
                    btob_ytd_year_ttc: totalBtobYtdYearTtc,
                    n1_ytd_year_ht: totalN1YtdYear,
                    n1_ytd_year_ttc: totalN1YtdYearTtc,
                    ecart_day_pct: totalBudgetDay > 0 ? ((totalRealDay - totalBudgetDay) / totalBudgetDay) * 100 : 0,
                    ecart_ytd_month_pct: totalBudgetYtdMonth > 0 ? ((totalRealYtdMonth - totalBudgetYtdMonth) / totalBudgetYtdMonth) * 100 : 0,
                    ecart_n1_ytd_month_pct: totalN1YtdMonth > 0 ? ((totalRealYtdMonth - totalN1YtdMonth) / totalN1YtdMonth) * 100 : 0,
                    ecart_ytd_year_pct: totalBudgetYtdYear > 0 ? ((totalRealYtdYear - totalBudgetYtdYear) / totalBudgetYtdYear) * 100 : 0,
                    ecart_n1_ytd_year_pct: totalN1YtdYear > 0 ? ((totalRealYtdYear - totalN1YtdYear) / totalN1YtdYear) * 100 : 0,
                });
            }
        } catch (error) {
            console.error('Error fetching consolidated:', error);
            setConsolidatedData(null);
            setConsolidatedComparison(null);
        } finally {
            setLoadingConsolidated(false);
        }
    }, [canViewConsolidated, selectedDate]);

    // Fetch ventes internes (BtoB) pour le restaurant sélectionné
    const fetchVentesInternes = useCallback(async () => {
        if (!selectedRestaurant || isConsolidatedMode) return;
        
        setLoadingVentesInternes(true);
        try {
            const response = await api.get(`/ventes-internes/ytd/${encodeURIComponent(selectedRestaurant.name)}?date_str=${selectedDate}`);
            setVentesInternesData(response.data);
        } catch (error) {
            console.error('Error fetching ventes internes:', error);
            setVentesInternesData(null);
        } finally {
            setLoadingVentesInternes(false);
        }
    }, [selectedRestaurant, selectedDate, isConsolidatedMode]);

    // Fetch monthly data for charts
    const [exportingChart, setExportingChart] = useState(false);
    const handleExportChart = async () => {
        try {
            setExportingChart(true);
            const restaurantName = isConsolidatedMode ? 'all' : selectedRestaurant?.name;
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/budget/monthly-comparison/${encodeURIComponent(restaurantName)}/export?date_str=${selectedDate}&compare=${chartCompareMode}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Erreur export');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ca_mensuel_${isConsolidatedMode ? 'consolide' : restaurantName?.replace(/ /g, '_')}_${selectedDate?.slice(0, 4)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Export Excel téléchargé');
        } catch (e) {
            console.error('Erreur export histogramme:', e);
            toast.error("Erreur lors de l'export");
        } finally {
            setExportingChart(false);
        }
    };

    const fetchMonthlyData = useCallback(async () => {
        if (!canViewConsolidated && !selectedRestaurant) return;
        
        try {
            const restaurantName = isConsolidatedMode ? 'all' : selectedRestaurant?.name;
            if (!restaurantName) return;
            
            const response = await budgetApi.getMonthlyComparison(restaurantName, selectedDate);
            setMonthlyData(response.data);
        } catch (error) {
            console.error('Error fetching monthly data:', error);
            setMonthlyData(null);
        }
    }, [canViewConsolidated, isConsolidatedMode, selectedRestaurant, selectedDate]);

    // Fetch open tables (CA en cours) - uniquement pour restaurant individuel avec POS
    const fetchOpenTablesData = useCallback(async () => {
        const restaurantsWithPOS = ["Ma biche sur le toit", "Jacques Balma", "Coup de bambou", "Compagnie Française", "Compagnie Busca", "Café Chouchou", "Sous les pins"];
        
        if (!selectedRestaurant || !restaurantsWithPOS.includes(selectedRestaurant.name) || isConsolidatedMode) {
            setOpenTablesData(null);
            return;
        }
        
        setLoadingOpenTables(true);
        try {
            const response = await posApi.getOpenTables(selectedRestaurant.name);
            setOpenTablesData(response.data);
        } catch (error) {
            console.error('Error fetching open tables:', error);
            setOpenTablesData(null);
        } finally {
            setLoadingOpenTables(false);
        }
    }, [selectedRestaurant, isConsolidatedMode]);

    // Fetch reservations data (Zenchef)
    const fetchReservationsData = useCallback(async () => {
        setLoadingReservations(true);
        try {
            const [resaRes, vipRes] = await Promise.all([
                reservationsApi.getSummary(selectedDate),
                reservationsApi.getVip(selectedDate)
            ]);
            setReservationsData(resaRes.data);
            setVipData(vipRes.data);
            setReservationsLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching reservations:', error);
            setReservationsData(null);
            setVipData(null);
        } finally {
            setLoadingReservations(false);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchDashboardData();
        fetchComparisonData();
        fetchConsolidatedData();
        fetchVentesInternes();
    }, [fetchDashboardData, fetchComparisonData, fetchConsolidatedData, fetchVentesInternes]);

    useEffect(() => {
        if (isConsolidatedMode || selectedRestaurant) {
            fetchMonthlyData();
        }
    }, [isConsolidatedMode, selectedRestaurant, selectedDate, fetchMonthlyData]);

    // Fetch open tables quand le restaurant change
    useEffect(() => {
        fetchOpenTablesData();
    }, [fetchOpenTablesData]);

    // Fetch reservations quand la date change + auto-refresh toutes les 2 minutes
    useEffect(() => {
        fetchReservationsData();
        
        // Auto-refresh des réservations toutes les 2 minutes (120 000 ms)
        const intervalId = setInterval(() => {
            console.log('🔄 Auto-refresh réservations Zenchef...');
            fetchReservationsData();
        }, 120000); // 2 minutes
        
        return () => clearInterval(intervalId);
    }, [fetchReservationsData]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleRestaurantSelect = (restaurant) => {
        // Vérifier si c'est Jacques Blagnac (caisse non connectée)
        if (restaurant.name === "Jacques Blagnac") {
            toast.error("Caisse non connectée", {
                description: "Nous n'arrivons pas encore à nous connecter à la caisse de Jacques Blagnac. Les données de ventes ne sont pas disponibles.",
                duration: 5000
            });
            return; // Ne pas sélectionner ce restaurant
        }
        setSelectedRestaurant(restaurant);
    };

    const dayData = dashboardData?.day_data;
    const selectedMonth = selectedDate ? new Date(selectedDate).getMonth() + 1 : 1;
    
    // Valeurs selon le mode d'affichage
    const caJour = displayMode === 'TTC' ? (dayData?.total_ttc || 0) : (dayData?.total_ht || 0);
    const caMois = displayMode === 'TTC' ? (dashboardData?.month_budget_ttc || dashboardData?.month_budget_ht || 0) : (dashboardData?.month_budget_ht || 0);
    const ytdMensuel = displayMode === 'TTC' ? (dashboardData?.ytd_month_ttc || dashboardData?.ytd_month_ht || 0) : (dashboardData?.ytd_month_ht || 0);
    const ytdAnnuel = displayMode === 'TTC' ? (dashboardData?.ytd_year_ttc || dashboardData?.ytd_year_ht || 0) : (dashboardData?.ytd_year_ht || 0);

    // ===== CALCUL FORECAST AVEC PONDÉRATION PROGRESSIVE =====
    // Méthode : éviter de sur-réagir au début du mois/année
    
    // Fonction pour obtenir la pondération mensuelle selon le jour
    const getMonthlyTrendWeight = (dayOfMonth) => {
        if (dayOfMonth <= 5) return 0;      // Jours 1-5: 0% tendance
        if (dayOfMonth <= 10) return 0.25;  // Jours 6-10: 25% tendance
        if (dayOfMonth <= 15) return 0.50;  // Jours 11-15: 50% tendance
        if (dayOfMonth <= 20) return 0.75;  // Jours 16-20: 75% tendance
        return 1.0;                          // Jours 21+: 100% tendance
    };
    
    // Fonction pour obtenir la pondération annuelle selon le mois
    const getAnnualTrendWeight = (month) => {
        // month: 0 = janvier, 11 = décembre
        if (month <= 2) return 0;           // Janvier-Mars: 0% tendance
        if (month <= 5) return 0.25;        // Avril-Juin: 25% tendance
        if (month <= 8) return 0.50;        // Juillet-Septembre: 50% tendance
        if (month <= 10) return 0.75;       // Octobre-Novembre: 75% tendance
        return 1.0;                          // Décembre: 100% tendance
    };
    
    const calculateForecast = () => {
        if (!selectedDate || !comparisonData) return null;
        
        const currentDate = new Date(selectedDate);
        const currentDay = currentDate.getDate();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        
        // ===== FORECAST MENSUEL =====
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const daysRemaining = daysInMonth - currentDay;
        
        // Données du mois
        const realYtdMonth = comparisonData?.real_ytd_month_ht || 0;
        const budgetYtdMonth = comparisonData?.budget_ytd_month_ht || 0;
        
        // Budget mensuel total - utiliser le VRAI budget mensuel depuis dashboardData
        // au lieu d'extrapoler depuis le YTD
        const budgetMonthTotal = dashboardData?.month_budget_ht || 
            (budgetYtdMonth > 0 ? (budgetYtdMonth / currentDay) * daysInMonth : 0);
        
        // Budget restant du mois
        const budgetRemaining = budgetMonthTotal - budgetYtdMonth;
        
        // Taux d'atteinte mensuel (éviter division par zéro)
        const tauxAtteinteMensuel = budgetYtdMonth > 0 ? realYtdMonth / budgetYtdMonth : 1;
        
        // Pondération de la tendance selon le jour du mois
        const ponderationMensuelle = getMonthlyTrendWeight(currentDay);
        
        // Budget restant ajusté par la tendance
        const budgetRestantAjuste = budgetRemaining * tauxAtteinteMensuel;
        
        // Forecast du reste du mois avec pondération progressive
        const forecastResteMois = 
            (budgetRemaining * (1 - ponderationMensuelle)) + 
            (budgetRestantAjuste * ponderationMensuelle);
        
        // Forecast mensuel final
        const forecastMonth = realYtdMonth + forecastResteMois;
        
        // ===== FORECAST ANNUEL =====
        const realYtdYear = comparisonData?.real_ytd_year_ht || 0;
        const budgetYtdYear = comparisonData?.budget_ytd_year_ht || 0;
        
        // Calculer le nombre de jours écoulés et total dans l'année
        const startOfYear = new Date(currentYear, 0, 1);
        const endOfYear = new Date(currentYear, 11, 31);
        const dayOfYear = Math.ceil((currentDate - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        const totalDaysInYear = Math.ceil((endOfYear - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        const daysRemainingYear = totalDaysInYear - dayOfYear;
        
        // Budget annuel total - utiliser la vraie valeur depuis l'API si disponible
        // Sinon fallback sur extrapolation
        const budgetYearTotal = comparisonData?.budget_year_total_ht > 0 
            ? comparisonData.budget_year_total_ht 
            : (budgetYtdYear > 0 ? (budgetYtdYear / dayOfYear) * totalDaysInYear : 0);
        
        // Budget mensuel total - utiliser la vraie valeur si disponible
        const budgetMonthTotalFromApi = comparisonData?.budget_month_total_ht || 0;
        
        // Budget restant de l'année (mois futurs)
        const budgetRestantAnnuel = budgetYearTotal - budgetYtdYear;
        
        // Taux d'atteinte annuel (éviter division par zéro)
        const tauxAtteinteAnnuel = budgetYtdYear > 0 ? realYtdYear / budgetYtdYear : 1;
        
        // Pondération annuelle selon le mois en cours
        const ponderationAnnuelle = getAnnualTrendWeight(currentMonth);
        
        // Budget futur ajusté par la tendance annuelle
        const budgetFuturAjuste = budgetRestantAnnuel * tauxAtteinteAnnuel;
        
        // Forecast des mois futurs avec pondération progressive
        const forecastMoisFuturs = 
            (budgetRestantAnnuel * (1 - ponderationAnnuelle)) + 
            (budgetFuturAjuste * ponderationAnnuelle);
        
        // Pour le forecast annuel, on prend:
        // - Réalisé des mois clôturés (inclus dans realYtdYear moins le mois en cours)
        // - Forecast du mois en cours
        // - Forecast des mois futurs
        // Simplification: realYtdYear inclut déjà les mois clôturés + le mois en cours
        // Donc: Forecast annuel = realYtdYear - realYtdMonth + forecastMonth + forecastMoisFuturs
        // Mais pour simplifier et être cohérent avec les données disponibles:
        const forecastYear = realYtdYear + forecastMoisFuturs;
        
        return {
            forecastMonth,
            forecastYear,
            budgetMonthTotal,
            budgetYearTotal,
            daysRemaining,
            daysRemainingYear,
            // Informations supplémentaires pour debug/affichage
            tauxAtteinteMensuel: tauxAtteinteMensuel * 100, // en %
            tauxAtteinteAnnuel: tauxAtteinteAnnuel * 100,   // en %
            ponderationMensuelle: ponderationMensuelle * 100, // en %
            ponderationAnnuelle: ponderationAnnuelle * 100,   // en %
            ecartForecastMonth: budgetMonthTotal > 0 ? ((forecastMonth - budgetMonthTotal) / budgetMonthTotal) * 100 : 0,
            ecartForecastYear: budgetYearTotal > 0 ? ((forecastYear - budgetYearTotal) / budgetYearTotal) * 100 : 0
        };
    };
    
    // Calcul du forecast consolidé (même logique)
    const calculateConsolidatedForecast = () => {
        if (!selectedDate || !consolidatedComparison) return null;
        
        const currentDate = new Date(selectedDate);
        const currentDay = currentDate.getDate();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();
        
        // ===== FORECAST MENSUEL CONSOLIDÉ =====
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const daysRemaining = daysInMonth - currentDay;
        
        const realYtdMonth = consolidatedComparison?.real_ytd_month_ht || 0;
        const budgetYtdMonth = consolidatedComparison?.budget_ytd_month_ht || 0;
        
        // Utiliser le VRAI budget mensuel total depuis consolidatedComparison
        // au lieu d'extrapoler depuis le YTD
        const budgetMonthTotal = consolidatedComparison?.budget_month_ht || 
            (budgetYtdMonth > 0 ? (budgetYtdMonth / currentDay) * daysInMonth : 0);
        
        const budgetRemaining = budgetMonthTotal - budgetYtdMonth;
        const tauxAtteinteMensuel = budgetYtdMonth > 0 ? realYtdMonth / budgetYtdMonth : 1;
        const ponderationMensuelle = getMonthlyTrendWeight(currentDay);
        
        const budgetRestantAjuste = budgetRemaining * tauxAtteinteMensuel;
        const forecastResteMois = 
            (budgetRemaining * (1 - ponderationMensuelle)) + 
            (budgetRestantAjuste * ponderationMensuelle);
        
        const forecastMonth = realYtdMonth + forecastResteMois;
        
        // ===== FORECAST ANNUEL CONSOLIDÉ =====
        const realYtdYear = consolidatedComparison?.real_ytd_year_ht || 0;
        const budgetYtdYear = consolidatedComparison?.budget_ytd_year_ht || 0;
        
        const startOfYear = new Date(currentYear, 0, 1);
        const endOfYear = new Date(currentYear, 11, 31);
        const dayOfYear = Math.ceil((currentDate - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        const totalDaysInYear = Math.ceil((endOfYear - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
        const daysRemainingYear = totalDaysInYear - dayOfYear;
        
        // Utiliser le VRAI budget annuel total depuis l'API consolidée
        const budgetYearTotal = consolidatedData?.total_budget_year_total_ht > 0 
            ? consolidatedData.total_budget_year_total_ht
            : (budgetYtdYear > 0 ? (budgetYtdYear / dayOfYear) * totalDaysInYear : 0);
        
        const budgetRestantAnnuel = budgetYearTotal - budgetYtdYear;
        const tauxAtteinteAnnuel = budgetYtdYear > 0 ? realYtdYear / budgetYtdYear : 1;
        const ponderationAnnuelle = getAnnualTrendWeight(currentMonth);
        
        const budgetFuturAjuste = budgetRestantAnnuel * tauxAtteinteAnnuel;
        const forecastMoisFuturs = 
            (budgetRestantAnnuel * (1 - ponderationAnnuelle)) + 
            (budgetFuturAjuste * ponderationAnnuelle);
        
        const forecastYear = realYtdYear + forecastMoisFuturs;
        
        return {
            forecastMonth,
            forecastYear,
            budgetMonthTotal,
            budgetYearTotal,
            daysRemaining,
            daysRemainingYear,
            tauxAtteinteMensuel: tauxAtteinteMensuel * 100,
            tauxAtteinteAnnuel: tauxAtteinteAnnuel * 100,
            ponderationMensuelle: ponderationMensuelle * 100,
            ponderationAnnuelle: ponderationAnnuelle * 100,
            ecartForecastMonth: budgetMonthTotal > 0 ? ((forecastMonth - budgetMonthTotal) / budgetMonthTotal) * 100 : 0,
            ecartForecastYear: budgetYearTotal > 0 ? ((forecastYear - budgetYearTotal) / budgetYearTotal) * 100 : 0
        };
    };
    
    const forecastData = calculateForecast();
    const consolidatedForecastData = calculateConsolidatedForecast();

    // Restaurants avec intégration POS (Trivec ou Popina)
    const restaurantsWithPOS = ["Ma biche sur le toit", "Jacques Balma", "Coup de bambou", "Compagnie Française", "Compagnie Busca", "Café Chouchou", "Sous les pins"];
    const hasPosIntegration = selectedRestaurant && restaurantsWithPOS.includes(selectedRestaurant.name);

    return (
        <div className="flex h-screen bg-background" data-testid="dashboard-page">
            <Sidebar />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header 
                    title={selectedRestaurant?.name || 'Tableau de bord'}
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    displayMode={displayMode}
                    onDisplayModeChange={setDisplayMode}
                />

                <main className="flex-1 overflow-auto p-6">
                    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
                        
                        {/* Restaurant Selector (for users with multiple restaurants) with Consolidated button */}
                        {canViewConsolidated && restaurants.length > 1 && (
                            <section>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-heading font-semibold">
                                        {isConsolidatedMode 
                                            ? (isDirection ? 'Tous les restaurants (Consolidé)' : 'Mes restaurants (Consolidé)')
                                            : 'Sélectionner un restaurant'}
                                    </h2>
                                    <Button
                                        variant={isConsolidatedMode ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setIsConsolidatedMode(!isConsolidatedMode)}
                                        className="gap-2"
                                    >
                                        <Building2 className="w-4 h-4" />
                                        {isConsolidatedMode ? 'Vue individuelle' : 'Consolidé restaurants'}
                                    </Button>
                                </div>
                                {!isConsolidatedMode && (
                                    <RestaurantSelector
                                        restaurants={restaurants}
                                        selectedId={selectedRestaurant?.id}
                                        onSelect={handleRestaurantSelect}
                                    />
                                )}
                            </section>
                        )}

                        {/* Comparaison Budget vs Réalisé - Consolidated Mode */}
                        {canViewConsolidated && isConsolidatedMode && (
                            <section className="space-y-6">
                                <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                                    {forecastMode ? (
                                        <>
                                            <Target className="w-5 h-5 text-amber-600" />
                                            Forecast 2026 (Consolidé)
                                        </>
                                    ) : (
                                        <>
                                            <TrendingUp className="w-5 h-5 text-primary" />
                                            Réalisé 2026 (Consolidé)
                                        </>
                                    )}
                                </h2>
                                
                                {loadingConsolidated ? (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {[1, 2, 3].map(i => (
                                            <Skeleton key={i} className="h-48 rounded-xl" />
                                        ))}
                                    </div>
                                ) : consolidatedComparison ? (
                                    <>
                                        {/* Bouton Forecast/Réalisé */}
                                        <div className="flex justify-end">
                                            <Button
                                                variant={forecastMode ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setForecastMode(!forecastMode)}
                                                className={cn(
                                                    "gap-2",
                                                    forecastMode && "bg-amber-600 hover:bg-amber-700"
                                                )}
                                                data-testid="forecast-toggle-btn"
                                            >
                                                <Target className="w-4 h-4" />
                                                {forecastMode ? 'Réalisé' : 'Forecast'}
                                            </Button>
                                        </div>
                                        
                                        {/* Row 1: CA du jour + Camembert + YTD Mensuel */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <ComparisonCardWithGauge
                                                label={`CA du jour ${displayMode} (Consolidé)`}
                                                budget={consolidatedComparison.budget_day_ht}
                                                real={consolidatedComparison.real_day_ht}
                                                realTtc={consolidatedComparison.real_day_ttc}
                                                n1={consolidatedComparison.n1_day_ht}
                                                n1Ttc={consolidatedComparison.n1_day_ttc}
                                                icon={Euro}
                                                displayMode={displayMode}
                                                caisse={consolidatedComparison.caisse_day_ht}
                                                caisseTtc={consolidatedComparison.caisse_day_ttc}
                                                btob={consolidatedComparison.btob_day_ht}
                                                btobTtc={consolidatedComparison.btob_day_ttc}
                                                restaurantsData={consolidatedData?.restaurants}
                                                dataKeyPrefix="day"
                                            />
                                            
                                            {/* Camembert CA par restaurant - seul élément affecté par le filtre HT/TTC */}
                                            <div className="bg-card border border-border shadow-sm p-4 rounded-xl">
                                                <p className="text-sm text-muted-foreground font-medium mb-2">
                                                    CA du jour par restaurant ({displayMode})
                                                </p>
                                                <div className="h-44">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={consolidatedData?.restaurants?.filter(r => {
                                                                    const value = displayMode === 'TTC' 
                                                                        ? (r.real_day_ttc !== undefined && r.real_day_ttc !== null ? r.real_day_ttc : r.real_day_ht) 
                                                                        : r.real_day_ht;
                                                                    return value > 0;
                                                                }).map(r => ({
                                                                    name: r.restaurant,
                                                                    value: displayMode === 'TTC' 
                                                                        ? (r.real_day_ttc !== undefined && r.real_day_ttc !== null ? r.real_day_ttc : r.real_day_ht) 
                                                                        : r.real_day_ht
                                                                })) || []}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius={35}
                                                                outerRadius={65}
                                                                dataKey="value"
                                                                paddingAngle={2}
                                                            >
                                                                {consolidatedData?.restaurants?.filter(r => {
                                                                    const value = displayMode === 'TTC' 
                                                                        ? (r.real_day_ttc !== undefined && r.real_day_ttc !== null ? r.real_day_ttc : r.real_day_ht) 
                                                                        : r.real_day_ht;
                                                                    return value > 0;
                                                                }).map((entry, index) => (
                                                                    <Cell 
                                                                        key={`cell-${index}`} 
                                                                        fill={['#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#3b82f6', '#eab308'][index % 6]} 
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip 
                                                                formatter={(value) => formatCurrency(value)}
                                                                contentStyle={{ 
                                                                    backgroundColor: 'hsl(var(--card))', 
                                                                    border: '1px solid hsl(var(--border))',
                                                                    borderRadius: '8px',
                                                                    fontSize: '12px'
                                                                }}
                                                            />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                            
                                            {forecastMode ? (
                                                <ForecastCard
                                                    forecastData={consolidatedForecastData}
                                                    label="Forecast Mensuel HT (Consolidé)"
                                                    subtitle="Atterrissage fin de mois"
                                                    icon={Target}
                                                    isMonth={true}
                                                />
                                            ) : (
                                                <ComparisonCard
                                                    label={`YTD Mensuel ${displayMode} (Consolidé)`}
                                                    budget={consolidatedComparison.budget_ytd_month_ht}
                                                    real={consolidatedComparison.real_ytd_month_ht}
                                                    realTtc={consolidatedComparison.real_ytd_month_ttc}
                                                    ecartBudget={consolidatedComparison.ecart_ytd_month_pct}
                                                    n1={consolidatedComparison.n1_ytd_month_ht}
                                                    n1Ttc={consolidatedComparison.n1_ytd_month_ttc}
                                                    ecartN1={consolidatedComparison.ecart_n1_ytd_month_pct}
                                                    subtitle="Tous restaurants combinés"
                                                    icon={CalendarDays}
                                                    displayMode={displayMode}
                                                    caisse={consolidatedComparison.caisse_ytd_month_ht}
                                                    caisseTtc={consolidatedComparison.caisse_ytd_month_ttc}
                                                    btob={consolidatedComparison.btob_ytd_month_ht}
                                                    btobTtc={consolidatedComparison.btob_ytd_month_ttc}
                                                    restaurantsData={consolidatedData?.restaurants}
                                                    dataKeyPrefix="ytd_month"
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Row 2: YTD Annuel ou Forecast Annuel */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {forecastMode ? (
                                                <ForecastCard
                                                    forecastData={consolidatedForecastData}
                                                    label="Forecast Annuel HT (Consolidé)"
                                                    subtitle="Atterrissage fin d'année"
                                                    icon={Target}
                                                    isMonth={false}
                                                />
                                            ) : (
                                                <ComparisonCard
                                                    label={`YTD Annuel ${displayMode} (Consolidé)`}
                                                    budget={consolidatedComparison.budget_ytd_year_ht}
                                                    real={consolidatedComparison.real_ytd_year_ht}
                                                    realTtc={consolidatedComparison.real_ytd_year_ttc}
                                                    ecartBudget={consolidatedComparison.ecart_ytd_year_pct}
                                                    n1={consolidatedComparison.n1_ytd_year_ht}
                                                    n1Ttc={consolidatedComparison.n1_ytd_year_ttc}
                                                    ecartN1={consolidatedComparison.ecart_n1_ytd_year_pct}
                                                    subtitle="Tous restaurants combinés"
                                                    icon={TrendingUp}
                                                    displayMode={displayMode}
                                                    caisse={consolidatedComparison.caisse_ytd_year_ht}
                                                    caisseTtc={consolidatedComparison.caisse_ytd_year_ttc}
                                                    btob={consolidatedComparison.btob_ytd_year_ht}
                                                    btobTtc={consolidatedComparison.btob_ytd_year_ttc}
                                                    restaurantsData={consolidatedData?.restaurants}
                                                    dataKeyPrefix="ytd_year"
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Carte BtoB Consolidée */}
                                        {(consolidatedData?.total_btob_ytd_month_ht > 0 || consolidatedData?.total_btob_ytd_year_ht > 0) && (
                                            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 shadow-sm p-4 rounded-xl hover:shadow-md transition-all duration-200" data-testid="btob-consolidated-card">
                                                <div className="flex items-start justify-between mb-3">
                                                    <p className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                                                        <Building2 className="w-4 h-4 text-emerald-500" />
                                                        BtoB Consolidé - Réalisé vs Budget vs N-1
                                                    </p>
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    {/* YTD Mensuel */}
                                                    <div className="space-y-2">
                                                        <p className="text-xs font-semibold text-muted-foreground uppercase">YTD Mensuel</p>
                                                        <div className="space-y-1.5">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">Réalisé</span>
                                                                <span className="text-sm font-bold text-emerald-600">{formatCurrency(consolidatedData?.total_btob_ytd_month_ht || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">Budget</span>
                                                                <span className="text-sm font-medium">{formatCurrency(consolidatedData?.total_btob_budget_ytd_month_ht || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">N-1</span>
                                                                <span className="text-sm font-medium text-slate-500">{formatCurrency(consolidatedData?.total_n1_btob_ytd_month_ht || 0)}</span>
                                                            </div>
                                                            <div className="pt-1.5 border-t border-emerald-500/20 space-y-1">
                                                                {(consolidatedData?.total_btob_budget_ytd_month_ht || 0) > 0 && (
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-muted-foreground">vs Budget</span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            ((consolidatedData?.total_btob_ytd_month_ht - consolidatedData?.total_btob_budget_ytd_month_ht) >= 0) ? "text-green-600" : "text-red-600"
                                                                        )}>
                                                                            {(((consolidatedData?.total_btob_ytd_month_ht - consolidatedData?.total_btob_budget_ytd_month_ht) / consolidatedData?.total_btob_budget_ytd_month_ht) * 100).toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {(consolidatedData?.total_n1_btob_ytd_month_ht || 0) > 0 && (
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-muted-foreground">vs N-1</span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            ((consolidatedData?.total_btob_ytd_month_ht - consolidatedData?.total_n1_btob_ytd_month_ht) >= 0) ? "text-green-600" : "text-red-600"
                                                                        )}>
                                                                            {(((consolidatedData?.total_btob_ytd_month_ht - consolidatedData?.total_n1_btob_ytd_month_ht) / consolidatedData?.total_n1_btob_ytd_month_ht) * 100).toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* YTD Annuel */}
                                                    <div className="space-y-2">
                                                        <p className="text-xs font-semibold text-muted-foreground uppercase">YTD Annuel</p>
                                                        <div className="space-y-1.5">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">Réalisé</span>
                                                                <span className="text-sm font-bold text-emerald-600">{formatCurrency(consolidatedData?.total_btob_ytd_year_ht || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">Budget</span>
                                                                <span className="text-sm font-medium">{formatCurrency(consolidatedData?.total_btob_budget_ytd_year_ht || 0)}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-xs text-muted-foreground">N-1</span>
                                                                <span className="text-sm font-medium text-slate-500">{formatCurrency(consolidatedData?.total_n1_btob_ytd_year_ht || 0)}</span>
                                                            </div>
                                                            <div className="pt-1.5 border-t border-emerald-500/20 space-y-1">
                                                                {(consolidatedData?.total_btob_budget_ytd_year_ht || 0) > 0 && (
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-muted-foreground">vs Budget</span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            ((consolidatedData?.total_btob_ytd_year_ht - consolidatedData?.total_btob_budget_ytd_year_ht) >= 0) ? "text-green-600" : "text-red-600"
                                                                        )}>
                                                                            {(((consolidatedData?.total_btob_ytd_year_ht - consolidatedData?.total_btob_budget_ytd_year_ht) / consolidatedData?.total_btob_budget_ytd_year_ht) * 100).toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {(consolidatedData?.total_n1_btob_ytd_year_ht || 0) > 0 && (
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-muted-foreground">vs N-1</span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            ((consolidatedData?.total_btob_ytd_year_ht - consolidatedData?.total_n1_btob_ytd_year_ht) >= 0) ? "text-green-600" : "text-red-600"
                                                                        )}>
                                                                            {(((consolidatedData?.total_btob_ytd_year_ht - consolidatedData?.total_n1_btob_ytd_year_ht) / consolidatedData?.total_n1_btob_ytd_year_ht) * 100).toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="bg-muted/50 rounded-xl p-4 text-center text-muted-foreground">
                                        Aucune donnée consolidée disponible
                                    </div>
                                )}
                            </section>
                        )}

                        {/* Histogramme CA Mensuel - Pour mode Consolidé uniquement */}
                        {isConsolidatedMode && monthlyData && (
                            <section>
                                <div className="bg-card border border-border rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-4">
                                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                                <BarChart3 className="w-5 h-5 text-primary" />
                                                CA mensuel (Consolidé) vs {chartCompareMode === 'budget' ? 'Budget' : 'N-1'}
                                            </h3>
                                            {/* Étiquette YTD Tendance */}
                                            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                YTD : {formatCurrency(monthlyData?.ytd_total || 0)}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant={chartCompareMode === 'budget' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setChartCompareMode('budget')}
                                            >
                                                Budget
                                            </Button>
                                            <Button
                                                variant={chartCompareMode === 'n1' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setChartCompareMode('n1')}
                                            >
                                                N-1
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleExportChart}
                                                disabled={exportingChart}
                                                data-testid="export-monthly-chart-consolidated-btn"
                                            >
                                                <Download className="w-4 h-4 mr-1" />
                                                {exportingChart ? 'Export...' : 'Excel'}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={monthlyData?.months || []} margin={{ top: 25, right: 20, left: 20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                                                <YAxis 
                                                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                                                    tick={{ fontSize: 11 }}
                                                />
                                                <Tooltip 
                                                    formatter={(value, name) => value !== null ? formatCurrency(value) : '-'}
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))', 
                                                        border: '1px solid hsl(var(--border))',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                
                                                {/* Barres Réalisé */}
                                                <Bar 
                                                    dataKey="real" 
                                                    name="Réalisé" 
                                                    fill="#8b5cf6" 
                                                    radius={[4, 4, 0, 0]}
                                                >
                                                    <LabelList
                                                        dataKey={chartCompareMode === 'budget' ? 'ecart_budget' : 'ecart_n1'}
                                                        position="top"
                                                        formatter={(value) => value !== null ? `${value >= 0 ? '+' : ''}${value}%` : ''}
                                                        style={{ fontSize: 9, fill: 'hsl(var(--foreground))' }}
                                                    />
                                                </Bar>
                                                
                                                {/* Barres Budget ou N-1 */}
                                                <Bar 
                                                    dataKey={chartCompareMode === 'budget' ? 'budget' : 'n1'} 
                                                    name={chartCompareMode === 'budget' ? 'Budget' : 'N-1'} 
                                                    fill="#d1d5db" 
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2 text-center">
                                        Les mois grisés montrent les budgets prévisionnels pour les mois à venir
                                    </p>
                                </div>
                            </section>
                        )}

                        {/* Comparaison Budget vs Réalisé (pour tous les restaurants) */}
                        {!isConsolidatedMode && selectedRestaurant && (
                            <section className="space-y-6">
                                <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                                    {forecastMode ? (
                                        <>
                                            <Target className="w-5 h-5 text-amber-600" />
                                            Forecast 2026
                                        </>
                                    ) : (
                                        <>
                                            <TrendingUp className="w-5 h-5 text-primary" />
                                            Réalisé 2026
                                        </>
                                    )}
                                </h2>
                                
                                {loadingComparison ? (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {[1, 2, 3].map(i => (
                                            <Skeleton key={i} className="h-48 rounded-xl" />
                                        ))}
                                    </div>
                                ) : comparisonData ? (
                                    <>
                                        {/* Message si pas de POS connecté */}
                                        {!hasPosIntegration && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm flex items-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                Caisse non connectée - Affichage des budgets uniquement
                                            </div>
                                        )}
                                        
                                        {/* Bouton Forecast/Réalisé */}
                                        <div className="flex justify-end">
                                            <Button
                                                variant={forecastMode ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setForecastMode(!forecastMode)}
                                                className={cn(
                                                    "gap-2",
                                                    forecastMode && "bg-amber-600 hover:bg-amber-700"
                                                )}
                                                data-testid="forecast-toggle-btn-individual"
                                            >
                                                <Target className="w-4 h-4" />
                                                {forecastMode ? 'Réalisé' : 'Forecast'}
                                            </Button>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <ComparisonCardWithGauge
                                                label="CA du jour"
                                                budget={comparisonData.budget_day_ht}
                                                real={comparisonData.real_day_ht}
                                                realTtc={comparisonData.real_day_ttc}
                                                n1={comparisonData.n1_day_ht}
                                                n1Ttc={comparisonData.n1_day_ttc}
                                                icon={Euro}
                                                displayMode={displayMode}
                                                caisse={comparisonData.caisse_day_ht}
                                                btob={comparisonData.btob_day_ht}
                                                caisseTtc={comparisonData.caisse_day_ttc}
                                                btobTtc={comparisonData.btob_day_ttc}
                                            />
                                            {forecastMode ? (
                                                <ForecastCard
                                                    forecastData={forecastData}
                                                    label="Forecast Mensuel HT"
                                                    subtitle="Atterrissage fin de mois"
                                                    icon={Target}
                                                    isMonth={true}
                                                />
                                            ) : (
                                                <ComparisonCard
                                                    label="YTD Mensuel HT"
                                                    budget={comparisonData.budget_ytd_month_ht}
                                                    real={comparisonData.real_ytd_month_ht}
                                                    ecartBudget={comparisonData.ecart_ytd_month_pct}
                                                    n1={comparisonData.n1_ytd_month_ht}
                                                    ecartN1={comparisonData.ecart_n1_ytd_month_pct}
                                                    subtitle="Depuis début du mois"
                                                    icon={CalendarDays}
                                                    caisse={comparisonData.real_ytd_month_ht - (comparisonData.btob_ytd_month_ht || 0)}
                                                    btob={comparisonData.btob_ytd_month_ht || 0}
                                                />
                                            )}
                                            {forecastMode ? (
                                                <ForecastCard
                                                    forecastData={forecastData}
                                                    label="Forecast Annuel HT"
                                                    subtitle="Atterrissage fin d'année"
                                                    icon={Target}
                                                    isMonth={false}
                                                />
                                            ) : (
                                                <ComparisonCard
                                                    label="YTD Annuel HT"
                                                    budget={comparisonData.budget_ytd_year_ht}
                                                    real={comparisonData.real_ytd_year_ht}
                                                    ecartBudget={comparisonData.ecart_ytd_year_pct}
                                                    n1={comparisonData.n1_ytd_year_ht}
                                                    ecartN1={comparisonData.ecart_n1_ytd_year_pct}
                                                    subtitle="Depuis début de l'année"
                                                    icon={TrendingUp}
                                                    caisse={comparisonData.real_ytd_year_ht - (comparisonData.btob_ytd_year_ht || 0)}
                                                    btob={comparisonData.btob_ytd_year_ht || 0}
                                                />
                                            )}
                                        </div>
                                        
                                        {/* CA en cours (tables ouvertes non encaissées) - seulement si POS connecté */}
                                        {hasPosIntegration && (
                                            <OpenTablesCard 
                                                data={openTablesData} 
                                                loading={loadingOpenTables} 
                                            />
                                        )}
                                        
                                        {/* BtoB Budget vs Réalisé */}
                                        <BtoBBudgetCard comparisonData={comparisonData} />
                                        
                                        {/* Carte Ventes Internes (Offerts direction) YTD Mensuel */}
                                        {ventesInternesData && ventesInternesData.internal_ytd_ht > 0 && (
                                            <div className="bg-card border border-border rounded-xl p-4" data-testid="ventes-internes-card">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Store className="w-5 h-5 text-orange-500" />
                                                    <h3 className="font-semibold">Ventes Internes (Offerts)</h3>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                                                        <div className="text-xs text-muted-foreground mb-1">YTD Mensuel HT</div>
                                                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                                                            {formatCurrency(ventesInternesData.internal_ytd_ht || 0)}
                                                        </div>
                                                    </div>
                                                    <div className="bg-muted/50 rounded-lg p-3">
                                                        <div className="text-xs text-muted-foreground mb-1">Poids sur CA Total HT</div>
                                                        <div className="text-xl font-bold">
                                                            {ventesInternesData.poids_ventes_internes_pct?.toFixed(1) || 0}%
                                                        </div>
                                                        <div className="text-xs text-muted-foreground mt-1">
                                                            sur {formatCurrency(ventesInternesData.ca_total_with_internal_ht || 0)} total
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Détail par type d'internal */}
                                                {ventesInternesData.internal_by_type && Object.keys(ventesInternesData.internal_by_type).length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-border">
                                                        <div className="text-xs text-muted-foreground mb-2">Détail par type</div>
                                                        <div className="space-y-1">
                                                            {Object.entries(ventesInternesData.internal_by_type).map(([typeName, data]) => (
                                                                <div key={typeName} className="flex items-center justify-between text-sm">
                                                                    <span className="text-muted-foreground">{typeName}</span>
                                                                    <div className="flex items-center gap-3">
                                                                        <span className="text-xs text-muted-foreground">({data.count} tickets)</span>
                                                                        <span className="font-medium text-orange-600 dark:text-orange-400">
                                                                            {formatCurrency(data.ht || 0)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Histogramme CA Mensuel - pour restaurant individuel */}
                                        {monthlyData && (
                                            <div className="bg-card border border-border rounded-xl p-4">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-4">
                                                        <h3 className="text-lg font-semibold flex items-center gap-2">
                                                            <BarChart3 className="w-5 h-5 text-primary" />
                                                            CA mensuel - {selectedRestaurant?.name} vs {chartCompareMode === 'budget' ? 'Budget' : 'N-1'}
                                                        </h3>
                                                        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                            YTD : {formatCurrency(monthlyData?.ytd_total || 0)}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant={chartCompareMode === 'budget' ? 'default' : 'outline'}
                                                            size="sm"
                                                            onClick={() => setChartCompareMode('budget')}
                                                        >
                                                            Budget
                                                        </Button>
                                                        <Button
                                                            variant={chartCompareMode === 'n1' ? 'default' : 'outline'}
                                                            size="sm"
                                                            onClick={() => setChartCompareMode('n1')}
                                                        >
                                                            N-1
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={handleExportChart}
                                                            disabled={exportingChart}
                                                            data-testid="export-monthly-chart-btn"
                                                        >
                                                            <Download className="w-4 h-4 mr-1" />
                                                            {exportingChart ? 'Export...' : 'Excel'}
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="h-80">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={monthlyData?.months || []} margin={{ top: 25, right: 20, left: 20, bottom: 5 }}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                            <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                                                            <YAxis 
                                                                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                                                                tick={{ fontSize: 11 }}
                                                            />
                                                            <Tooltip 
                                                                formatter={(value, name) => value !== null ? formatCurrency(value) : '-'}
                                                                contentStyle={{ 
                                                                    backgroundColor: 'hsl(var(--card))', 
                                                                    border: '1px solid hsl(var(--border))',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                            <Legend />
                                                            <Bar 
                                                                dataKey="real" 
                                                                name="Réalisé" 
                                                                fill="#8b5cf6" 
                                                                radius={[4, 4, 0, 0]}
                                                            >
                                                                <LabelList
                                                                    dataKey={chartCompareMode === 'budget' ? 'ecart_budget' : 'ecart_n1'}
                                                                    position="top"
                                                                    formatter={(value) => value !== null ? `${value >= 0 ? '+' : ''}${value}%` : ''}
                                                                    style={{ fontSize: 9, fill: 'hsl(var(--foreground))' }}
                                                                />
                                                            </Bar>
                                                            <Bar 
                                                                dataKey={chartCompareMode === 'budget' ? 'budget' : 'n1'} 
                                                                name={chartCompareMode === 'budget' ? 'Budget' : 'N-1'} 
                                                                fill="#d1d5db" 
                                                                radius={[4, 4, 0, 0]}
                                                            />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-2 text-center">
                                                    Les mois grisés montrent les budgets prévisionnels pour les mois à venir
                                                </p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="bg-muted/50 rounded-xl p-4 text-center text-muted-foreground">
                                        {comparisonData?.error_message || "Données réalisées non disponibles"}
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardPage;
